import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type Dispatch,
  type SetStateAction,
} from 'react'
import {getRelaySessionState, observeRelayChat, subscribeRelayRegistry} from '@github-ui/ahp-relay/relay-registry'
import {AhpChatStore} from '@github-ui/ahp-relay/ahp-chat-store'
import {resolveSubagent, type SubagentHandle} from '@github-ui/ahp-relay/ahp-session-selectors'
import type {ChatState, ToolCallState} from '@microsoft/agent-host-protocol'

export interface UseAhpChatStateOptions {
  taskId: string | undefined
  channel: string
  enabled: boolean
}

export interface UseAhpChatStateResult {
  state: ChatState | undefined
  error: string | undefined
}

interface AhpChatFailure {
  channel: string
  message: string
}

function deferFailure(
  setFailure: Dispatch<SetStateAction<AhpChatFailure | undefined>>,
  channel: string,
  cause: unknown,
) {
  const message = cause instanceof Error ? cause.message : String(cause)
  queueMicrotask(() => setFailure({channel, message}))
}

function createChatStore(channel: string): AhpChatStore {
  if (!channel) throw new Error('chat channel is required')
  return new AhpChatStore()
}

/**
 * Scoped child-chat subscription used only by expanded subagent rows. The root
 * established session owns the root lease; this hook never acquires one.
 */
export function useAhpChatState({taskId, channel, enabled}: UseAhpChatStateOptions): UseAhpChatStateResult {
  const store = useMemo(() => createChatStore(channel), [channel])
  const [failure, setFailure] = useState<AhpChatFailure>()

  useEffect(() => {
    if (!enabled || !taskId) return
    try {
      return observeRelayChat(taskId, channel, {
        onSnapshot: state => {
          setFailure(undefined)
          store.applySnapshot(state)
        },
        onAction: action => store.applyAction(action),
        onError: message => setFailure({channel, message}),
      })
    } catch (subscriptionError) {
      deferFailure(setFailure, channel, subscriptionError)
    }
  }, [channel, enabled, store, taskId])

  const subscribe = useCallback((onStoreChange: () => void) => store.subscribe(onStoreChange), [store])
  const state = useSyncExternalStore(
    subscribe,
    () => store.getSnapshot(),
    () => undefined,
  )

  return {state, error: failure?.channel === channel ? failure.message : undefined}
}

export function useSubagentPresence(
  taskId: string | undefined,
  parentChatResource: string | undefined,
  enabled: boolean,
): (toolCall: ToolCallState) => boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!enabled) return () => {}
      return subscribeRelayRegistry(onStoreChange)
    },
    [enabled],
  )
  const sessionState = useSyncExternalStore(
    subscribe,
    () => (enabled ? getRelaySessionState(taskId) : undefined),
    () => undefined,
  )

  return useCallback(
    (toolCall: ToolCallState) =>
      enabled &&
      Boolean(resolveSubagent(toolCall, sessionState?.chats ?? [], parentChatResource, sessionState !== undefined)),
    [enabled, parentChatResource, sessionState],
  )
}

export function useAhpSubagent(
  taskId: string | undefined,
  parentChatResource: string | undefined,
  toolCall: ToolCallState,
  onUnavailable: () => void,
  enabled = true,
): SubagentHandle | undefined {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!enabled) return () => {}
      const initialSessionState = getRelaySessionState(taskId)
      let previous = resolveSubagent(
        toolCall,
        initialSessionState?.chats ?? [],
        parentChatResource,
        initialSessionState !== undefined,
      )
      return subscribeRelayRegistry(() => {
        const nextSessionState = getRelaySessionState(taskId)
        const next = resolveSubagent(
          toolCall,
          nextSessionState?.chats ?? [],
          parentChatResource,
          nextSessionState !== undefined,
        )
        if (previous && !next) onUnavailable()
        previous = next
        onStoreChange()
      })
    },
    [enabled, onUnavailable, parentChatResource, taskId, toolCall],
  )
  const sessionState = useSyncExternalStore(
    subscribe,
    () => (enabled ? getRelaySessionState(taskId) : undefined),
    () => undefined,
  )

  if (!enabled) return undefined
  return resolveSubagent(toolCall, sessionState?.chats ?? [], parentChatResource, sessionState !== undefined)
}
